(() => {
  if (window.regirlShippingProtectionLoaded) return;
  window.regirlShippingProtectionLoaded = true;

  const lowVariantId = 68294372229343;
  const highVariantId = 68294374621407;
  const threshold = 10000000;
  const protectionIds = [lowVariantId, highVariantId];
  const root = window.Shopify?.routes?.root || "/";
  const preferenceKey = "regirlShippingProtectionPreference";

  let queue = Promise.resolve();
  let reconcileTimer;
  let observedSignature = null;

  const getPreference = () => {
    try {
      return window.sessionStorage.getItem(preferenceKey);
    } catch (_) {
      return null;
    }
  };

  const setPreference = (value) => {
    try {
      if (value === null) window.sessionStorage.removeItem(preferenceKey);
      else window.sessionStorage.setItem(preferenceKey, value);
    } catch (_) {}
  };

  const request = async (path, body) => {
    const response = await fetch(`${root}${path}`, {
      method: body ? "POST" : "GET",
      credentials: "same-origin",
      headers: {
        Accept: "application/json",
        ...(body ? { "Content-Type": "application/json" } : {})
      },
      ...(body ? { body: JSON.stringify(body) } : {})
    });
    const data = await response.json();
    if (!response.ok || data.errors) {
      throw new Error(data.description || "Unable to update Shipping Protection.");
    }
    return data;
  };

  const serialize = (task) => {
    const result = queue.then(task);
    queue = result.catch(() => {});
    return result;
  };

  const protectionLines = (cart) =>
    (cart.items || []).filter((item) => protectionIds.includes(Number(item.variant_id)));

  const merchandiseLines = (cart) =>
    (cart.items || []).filter((item) => !protectionIds.includes(Number(item.variant_id)));

  const desiredVariantId = (cart) => {
    const merchandiseTotal = merchandiseLines(cart).reduce(
      (total, item) => total + item.final_line_price,
      0
    );
    return merchandiseTotal >= threshold ? highVariantId : lowVariantId;
  };

  const isCorrect = (cart, enabled) => {
    const lines = protectionLines(cart);
    if (!enabled) return lines.length === 0;
    return lines.length === 1 && Number(lines[0].variant_id) === desiredVariantId(cart) && lines[0].quantity === 1;
  };

  const sectionIds = () => {
    const ids = [];
    const mainCart = document.querySelector("#mainCartForm");
    if (mainCart?.dataset.sectionId) ids.push(mainCart.dataset.sectionId);
    if (document.querySelector("sht-cart-drwr, .js-cart-drawer-wrapper")) ids.push("cart-drawer");
    return [...new Set(ids)];
  };

  const parseSection = (html, selector) =>
    new DOMParser().parseFromString(html, "text/html").querySelector(selector);

  const renderSections = (state, ids) => {
    if (!state?.sections) return;

    const mainId = document.querySelector("#mainCartForm")?.dataset.sectionId;
    const currentMainTotal = document.querySelector(".js-cart-form-content-cart-total");
    const nextMainTotal = mainId && state.sections[mainId] && parseSection(
      state.sections[mainId],
      ".js-cart-form-content-cart-total"
    );
    if (currentMainTotal && nextMainTotal) currentMainTotal.innerHTML = nextMainTotal.innerHTML;

    if (!ids.includes("cart-drawer") || !state.sections["cart-drawer"]) return;
    const currentWrapper = document.querySelector(".js-cart-drawer-wrapper");
    const nextWrapper = parseSection(state.sections["cart-drawer"], ".js-cart-drawer-wrapper");
    if (!currentWrapper || !nextWrapper) return;

    const currentFooter = currentWrapper.querySelector(".drawer__footer");
    const nextFooter = nextWrapper.querySelector(".drawer__footer");
    if (currentFooter && nextFooter) currentFooter.innerHTML = nextFooter.innerHTML;
    else currentWrapper.innerHTML = nextWrapper.innerHTML;
  };

  const setTogglePresentation = (toggle, enabled) => {
    const wrapper = toggle.closest("[data-shipping-protection-toggle]");
    if (!wrapper) return;
    const price = wrapper.querySelector(".shipping-protection-toggle__heading strong")?.textContent?.match(/\([^)]*\)/)?.[0] || "";
    wrapper.dataset.selected = enabled ? "true" : "false";
    toggle.setAttribute("aria-checked", enabled ? "true" : "false");
    toggle.setAttribute("aria-label", enabled ? "Remove Shipping Protection" : `Add Shipping Protection${price ? ` for ${price}` : ""}`);
  };

  const synchronize = async (enabled) => {
    const cart = await request("cart.js");
    const merchandise = merchandiseLines(cart);
    const optedIn = enabled ?? getPreference() !== "off";
    const lines = protectionLines(cart);

    if (!merchandise.length && !lines.length) return { state: null, ids: sectionIds() };
    if (merchandise.length && isCorrect(cart, optedIn)) return { state: null, ids: sectionIds() };

    const ids = sectionIds();
    const updates = { [lowVariantId]: 0, [highVariantId]: 0 };
    if (merchandise.length && optedIn) updates[desiredVariantId(cart)] = 1;

    const state = await request("cart/update.js", {
      updates,
      sections: ids,
      sections_url: window.location.pathname
    });

    const shouldBeEnabled = merchandise.length > 0 && optedIn;
    if (!isCorrect(state, shouldBeEnabled)) {
      throw new Error("Shipping Protection could not be confirmed. Please refresh and try again.");
    }
    return { state, ids };
  };

  const showError = (error) => console.error("Unable to update Shipping Protection.", error);

  const visibleCartSignature = () => {
    const rows = [...document.querySelectorAll(".cart-drawer__product, .cart-item")];
    const entries = rows.map((row) => `${row.id}:${row.querySelector('input[type="number"]')?.value || ""}`);
    return `${entries.join("|")}|${document.querySelector("[data-shipping-protection-toggle]") ? "toggle" : "no-toggle"}`;
  };

  const reconcileFromCartChange = () => {
    const signature = visibleCartSignature();
    if (signature === observedSignature) return;
    observedSignature = signature;
    if (!document.querySelector("[data-shipping-protection-toggle]")) return;

    clearTimeout(reconcileTimer);
    reconcileTimer = window.setTimeout(() => {
      serialize(() => synchronize())
        .then(({ state, ids }) => renderSections(state, ids))
        .catch(showError);
    }, 180);
  };

  document.addEventListener(
    "click",
    (event) => {
      const toggle = event.target.closest?.("[data-shipping-protection-toggle] .shipping-protection-toggle__switch");
      if (!toggle || toggle.getAttribute("aria-busy") === "true") return;

      event.preventDefault();
      event.stopImmediatePropagation();
      const enabled = toggle.closest("[data-shipping-protection-toggle]")?.dataset.selected !== "true";
      const previousPreference = getPreference();
      toggle.setAttribute("aria-busy", "true");
      setTogglePresentation(toggle, enabled);
      setPreference(enabled ? "on" : "off");

      serialize(() => synchronize(enabled))
        .then(({ state, ids }) => renderSections(state, ids))
        .catch((error) => {
          setPreference(previousPreference);
          setTogglePresentation(toggle, !enabled);
          showError(error);
        })
        .finally(() => toggle.removeAttribute("aria-busy"));
    },
    true
  );

  new MutationObserver(reconcileFromCartChange).observe(document.documentElement, {
    childList: true,
    subtree: true
  });
  reconcileFromCartChange();
})();
